import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ShieldCheck, ShieldAlert, Upload, FileText, Clock } from "lucide-react";
import { toast } from "sonner";

export default function DomainVerification() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/workspace/verify/status");
      setStatus(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <p className="text-sm text-[#555568]">Loading verification status…</p>;
  if (!status) return null;

  if (status.status === "not_applicable") {
    return null; // user has no workspace; nothing to show
  }

  return (
    <section className="rounded-xl border border-[#E8ECF2] bg-white p-6" data-testid="domain-verification-section">
      <div className="flex items-start gap-3 mb-4">
        {status.verified
          ? <ShieldCheck className="text-[#1D9E75] mt-1" size={20} />
          : <ShieldAlert className="text-[#BA7517] mt-1" size={20} />}
        <div>
          <h2 className="font-display text-xl font-bold text-[#1A1A2E]">Workspace domain verification</h2>
          <p className="text-sm text-[#555568] mt-1">
            {status.verified
              ? "Your workspace is verified."
              : status.needs_verification
              ? "Your team is using a personal email domain. Verify the business to unlock full Sync / Momentum / Command features."
              : "Your domain is approved automatically because everyone uses a company email address."}
          </p>
        </div>
      </div>

      <StatusBlock status={status} />

      {!status.verified && status.status !== "pending" && (
        <DeclareForm onSubmitted={load} />
      )}

      {status.status === "pending" && (
        <p className="text-xs text-[#555568] italic mt-3">Submitted. The S-Tier review team will respond within 24 hours.</p>
      )}

      {status.status === "rejected" && (
        <div className="mt-4">
          <p className="text-sm text-[#E24B4A] mb-3">
            Previous submission rejected. {status.decision_note ? `Note: ${status.decision_note}` : ""}
          </p>
          <DeclareForm onSubmitted={load} retry />
        </div>
      )}
    </section>
  );
}

function StatusBlock({ status }) {
  const colorMap = {
    not_started: ["#555568", "Not submitted"],
    pending: ["#BA7517", "Pending S-Tier review"],
    approved: ["#1D9E75", "Verified"],
    rejected: ["#E24B4A", "Rejected"],
  };
  const [color, label] = colorMap[status.status] || colorMap.not_started;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-xs">
      <Field k="Status" v={label} color={color} testid="domain-verify-status" />
      <Field k="Workspace" v={status.workspace_id || "-"} mono />
      <Field k="Email domain" v={status.user_email_domain || "-"} mono />
      <Field k="Submitted" v={status.declared_at ? new Date(status.declared_at).toLocaleDateString() : "-"} mono />
    </div>
  );
}

function Field({ k, v, mono, color, testid }) {
  return (
    <div className="rounded-md border border-[#E8ECF2] bg-[#F8F9FC] p-3" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-[0.18em] text-[#555568]">{k}</p>
      <p className={`mt-1 text-sm ${mono ? "font-mono" : "font-semibold"}`} style={color ? { color } : undefined}>{v}</p>
    </div>
  );
}

function DeclareForm({ onSubmitted, retry }) {
  const [form, setForm] = useState({
    kind: "self_declaration",
    declarant_name: "",
    role_title: "",
    declared_business_name: "",
    notes: "",
    doc_data_uri: null,
    doc_filename: null,
  });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const onFile = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large. Max 5 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setForm((s) => ({ ...s, doc_data_uri: reader.result, doc_filename: file.name }));
    };
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!form.declarant_name.trim() || !form.role_title.trim()) {
      toast.error("Name and role title are required.");
      return;
    }
    if (form.kind === "domain_proof" && !form.doc_data_uri) {
      toast.error("Upload a proof document for domain_proof verification.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/workspace/verify/declare", form);
      toast.success("Submitted. The S-Tier review team will respond within 24 hours.");
      onSubmitted();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3 mt-4 border-t border-[#E8ECF2] pt-5" data-testid="domain-verify-form">
      <h3 className="font-display text-base font-bold text-[#1A1A2E]">
        {retry ? "Resubmit verification" : "Submit verification"}
      </h3>

      <div>
        <Label className="text-xs uppercase tracking-[0.18em] text-[#555568]">Verification type</Label>
        <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
          <SelectTrigger className="mt-1" data-testid="domain-verify-kind"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="self_declaration">Self-declaration (no document required)</SelectItem>
            <SelectItem value="domain_proof">Document upload (recommended)</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-[#555568] mt-1">
          {form.kind === "self_declaration"
            ? "Sign a declaration that this is a legitimate small business. Subject to audit."
            : "Upload a business registration certificate, utility bill, or domain ownership proof. PDF / PNG / JPG / WEBP up to 5 MB."}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs uppercase tracking-[0.18em] text-[#555568]">Your full name</Label>
          <Input
            value={form.declarant_name}
            onChange={(e) => setForm({ ...form, declarant_name: e.target.value })}
            data-testid="domain-verify-name"
            className="mt-1"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.18em] text-[#555568]">Role / title</Label>
          <Input
            value={form.role_title}
            onChange={(e) => setForm({ ...form, role_title: e.target.value })}
            placeholder="e.g. Founder, Operations Lead"
            data-testid="domain-verify-role"
            className="mt-1"
          />
        </div>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-[0.18em] text-[#555568]">Declared business name (optional)</Label>
        <Input
          value={form.declared_business_name}
          onChange={(e) => setForm({ ...form, declared_business_name: e.target.value })}
          data-testid="domain-verify-business"
          className="mt-1"
        />
      </div>

      {form.kind === "domain_proof" && (
        <div>
          <Label className="text-xs uppercase tracking-[0.18em] text-[#555568]">Proof document</Label>
          <div className="mt-1">
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/*"
              onChange={(e) => onFile(e.target.files?.[0])}
              data-testid="domain-verify-file"
              className="block w-full text-sm text-[#1A1A2E] file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-[#1A3A5C] file:text-white file:cursor-pointer"
            />
          </div>
          {form.doc_filename && (
            <div className="mt-2 flex items-center gap-2 text-xs text-[#555568]">
              <FileText size={12} /> {form.doc_filename}
            </div>
          )}
        </div>
      )}

      <div>
        <Label className="text-xs uppercase tracking-[0.18em] text-[#555568]">Additional notes (optional)</Label>
        <Textarea
          rows={3}
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          data-testid="domain-verify-notes"
          className="mt-1"
        />
      </div>

      <div className="flex items-center gap-3 pt-1">
        <Button onClick={submit} disabled={busy} className="bg-[#2C6BAC] hover:bg-[#1A3A5C] text-white" data-testid="domain-verify-submit">
          <Upload size={14} className="mr-1.5" /> {busy ? "Submitting…" : "Submit for review"}
        </Button>
        <span className="text-xs text-[#555568] flex items-center gap-1">
          <Clock size={12} /> Reviewed within 24h by S-Tier
        </span>
      </div>
    </div>
  );
}
